import type { App } from '@grafikr/components';
import v2AnnouncementBar from 'layout/v2-announcement-bar';
import v2Header from 'layout/v2-header';

import hasMatchingSite from 'utils/loaders/has-matching-site';
import intersectionLoader from 'utils/loaders/intersection';
import onElementInteraction from 'utils/loaders/on-element-interaction';
import onEvent from '../utils/loaders/on-event';

export default (app: App) => {
  app.add({
    // Layout
    'v2-announcement-bar': v2AnnouncementBar,
    'v2-header': v2Header,
    'mobile-menu': [
      onElementInteraction,
      () => import(/* webpackChunkName: "layout-mobile-menu" */ 'layout/mobile-menu'),
    ],
    'v2-search-modal': [
      onElementInteraction,
      () => import(/* webpackChunkName: "layout-v2-search-modal" */ 'layout/v2-search-modal'),
    ],

    // Components
    'v2-click-event': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-v2-click-event" */ 'components/v2-click-event'),
    ],
    'v2-countdown': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-v2-countdown" */ 'components/v2-countdown'),
    ],
    'v2-countdown-simple': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-v2-countdown-simple" */ 'components/v2-countdown-simple'
        ),
    ],
    'v2-display-name-region': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-v2-display-name-region" */ 'components/v2-display-name-region'
        ),
    ],
    'v2-geo-modal': [
      [onElementInteraction, hasMatchingSite],
      () => import(/* webpackChunkName: "component-v2-geo-modal" */ 'components/v2-geo-modal'),
    ],
    'v2-geo-selector-form': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-v2-geo-selector-form" */ '../components/v2-geo-selector-form'
        ),
    ],
    'v2-homepage-link': [
      intersectionLoader,
      () =>
        import(/* webpackChunkName: "component-v2-homepage-link" */ 'components/v2-homepage-link'),
    ],
    'v2-modal': [
      onElementInteraction,
      () => import(/* webpackChunkName: "component-v2-modal" */ 'components/v2-modal'),
    ],
    'v2-product-carousel': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-v2-product-carousel" */ 'components/v2-product-carousel'),
    ],
    'v2-progress-bar': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-v2-progress-bar" */ 'components/v2-progress-bar'),
    ],
    'v2-tabs': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-v2-tabs" */ 'components/v2-tabs'),
    ],
    'home-link': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-home-link" */ 'components/home-link'),
    ],
    'v2-shipping-countdown': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-v2-shipping-countdown" */ 'components/v2-shipping-countdown'
        ),
    ],

    // Pages
    'v2-account-actions': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "pages-customer-v2-account-actions" */ 'pages/customer/v2-account-actions'
        ),
    ],
    'v2-account-benefits': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "pages-customer-v2-account-benefits" */ 'pages/customer/v2-account-benefits'
        ),
    ],
    'v2-account-erasure-forms': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "pages-customer-v2-account-erasure-forms" */ 'pages/customer/v2-account-erasure-forms'
        ),
    ],
    'v2-account-logout': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "pages-customer-v2-account-logout" */ 'pages/customer/v2-account-logout'
        ),
    ],
    'v2-account-loading': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "pages-customer-v2-account-loading" */ 'pages/customer/v2-account-loading'
        ),
    ],
    'v2-account-navigation': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "pages-customer-v2-account-navigation" */ 'pages/customer/v2-account-navigation'
        ),
    ],
    'v2-account-point-shop': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "pages-customer-v2-account-point-shop" */ 'pages/customer/v2-account-point-shop'
        ),
    ],
    'v2-account-profile-form': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "pages-customer-v2-account-profile-form" */ 'pages/customer/v2-account-profile-form'
        ),
    ],
    'v2-account-referral-code': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "pages-customer-v2-account-referral-code" */ 'pages/customer/v2-account-referral-code'
        ),
    ],
    'v2-account-referral-coupon': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "pages-customer-v2-account-referral-coupon" */ 'pages/customer/v2-account-referral-coupon'
        ),
    ],
    'v2-account-referral-progress': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "pages-customer-v2-account-referral-progress" */ '../pages/customer/v2-account-referral-progress'
        ),
    ],
    'v2-account-retail-orders': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "pages-customer-v2-account-retail-orders" */ 'pages/customer/v2-account-retail-orders'
        ),
    ],
    'v2-account-reward-history': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "pages-customer-v2-account-reward-history" */ 'pages/customer/v2-account-reward-history'
        ),
    ],
    'v2-account-tier': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "pages-customer-v2-account-tier" */ 'pages/customer/v2-account-tier'
        ),
    ],
    'v2-account-tier-progress': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "pages-customer-v2-account-tier-progress" */ 'pages/customer/v2-account-tier-progress'
        ),
    ],
    'v2-account-tier-table': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "pages-customer-v2-account-tier-table" */ 'pages/customer/v2-account-tier-table'
        ),
    ],
    'v2-account-vouchers': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "pages-customer-v2-account-vouchers" */ 'pages/customer/v2-account-vouchers'
        ),
    ],
    'v2-relewise-carousel': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-v2-relewise-carousel" */ 'components/v2-relewise-carousel'),
    ],
    'v2-group-price': [
      [intersectionLoader, onEvent],
      () => import(/* webpackChunkName: "component-v2-group-price" */ 'components/v2-group-price'),
    ],
    'v2-cart-drawer': [
      [onElementInteraction, ...(theme.pageType !== 'cart' ? [onEvent] : [])],
      () => import(/* webpackChunkName: "component-cart-drawer" */ 'components/v2-cart-drawer'),
    ],
    'v2-product-upsell-item': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-v2-product-upsell-item" */ 'components/v2-product-upsell-item'),
    ],

    // Product
    'v2-suit-selector': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-v2-suit-selector" */ 'pages/product/v2-suit-selector'
        ),
    ],
  });
};
